import React, { useEffect, useState, useRef, useCallback } from "react";
import { useUser } from "../context/UserContext";
import { supabase } from "../lib/supabase";
import NewMessageModal from "./NewMessageModal";
import PostViewerModal from "./PostViewerModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFaceSmile } from "@fortawesome/free-regular-svg-icons";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 20;

function ChatPanel({ conversation, onMessageChange }) {
    const { user } = useUser();
    const navigate = useNavigate();

    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [showNewMessage, setShowNewMessage] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);
    const [menuState, setMenuState] = useState({ id: null, top: 0, left: 0 });
    const [showDetailsPanel, setShowDetailsPanel] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // Post viewer when clicking a shared post in chat
    const [selectedPostMsg, setSelectedPostMsg] = useState(null); // { post, profile }

    // Pagination
    const [loadingInitial, setLoadingInitial] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const isFetchingRef = useRef(false);
    const hasMoreRef = useRef(true);

    const bottomRef = useRef(null);
    const chatMessagesRef = useRef(null);
    const inputRef = useRef(null);

    // ── FETCH LATEST (initial) ─────────────────────────────────────────────────
    const fetchInitial = useCallback(async () => {
        if (!conversation || !user) return;
        setLoadingInitial(true);
        offsetRef.current = 0;
        hasMoreRef.current = true;
        setHasMore(true);

        const { data, error } = await supabase
            .from("messages")
            .select("*")
            .eq("conversation_id", conversation.id)
            .order("created_at", { ascending: false }) // newest first
            .range(0, PAGE_SIZE - 1);

        if (!error && data) {
            const ordered = [...data].reverse(); // flip to chronological
            setMessages(ordered);

            const fetched = data.length;
            offsetRef.current = fetched;
            if (fetched < PAGE_SIZE) {
                hasMoreRef.current = false;
                setHasMore(false);
            }
        }

        // Mark messages as read
        await supabase
            .from("messages")
            .update({ is_read: true })
            .eq("conversation_id", conversation.id)
            .neq("sender_id", user.id);

        setLoadingInitial(false);

        // Scroll to bottom after initial load
        setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: "auto" });
        }, 50);
    }, [conversation?.id, user]);

    // ── FETCH OLDER MESSAGES (load more) ──────────────────────────────────────
    const fetchMore = useCallback(async () => {
        if (!conversation || !user) return;
        if (isFetchingRef.current || !hasMoreRef.current) return;

        isFetchingRef.current = true;
        setLoadingMore(true);

        const container = chatMessagesRef.current;
        // Save scroll position so we can restore it after prepending
        const prevScrollHeight = container?.scrollHeight || 0;

        const { data, error } = await supabase
            .from("messages")
            .select("*")
            .eq("conversation_id", conversation.id)
            .order("created_at", { ascending: false })
            .range(offsetRef.current, offsetRef.current + PAGE_SIZE - 1);

        if (!error && data) {
            const ordered = [...data].reverse(); // oldest first
            offsetRef.current += data.length;

            if (data.length < PAGE_SIZE) {
                hasMoreRef.current = false;
                setHasMore(false);
            }

            setMessages(prev => [...ordered, ...prev]);

            // Restore scroll position — prevent jump to top
            requestAnimationFrame(() => {
                if (container) {
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - prevScrollHeight;
                }
            });
        }

        setLoadingMore(false);
        isFetchingRef.current = false;
    }, [conversation?.id, user]);

    // ── SCROLL LISTENER — load more when near top ──────────────────────────────
    useEffect(() => {
        const container = chatMessagesRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (container.scrollTop < 80 && !isFetchingRef.current && hasMoreRef.current) {
                fetchMore();
            }
        };

        container.addEventListener("scroll", handleScroll, { passive: true });
        return () => container.removeEventListener("scroll", handleScroll);
    }, [fetchMore, conversation?.id]);

    // ── REALTIME subscription for new incoming messages ────────────────────────
    useEffect(() => {
        if (!conversation) return;

        const channel = supabase
            .channel(`messages-${conversation.id}`)
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "messages",
                    filter: `conversation_id=eq.${conversation.id}`,
                },
                (payload) => {
                    const newMsg = payload.new;
                    // Only append if it's from the other user (our own messages are added optimistically)
                    if (newMsg.sender_id !== user?.id) {
                        setMessages(prev => {
                            // Avoid duplicates
                            if (prev.some(m => m.id === newMsg.id)) return prev;
                            return [...prev, newMsg];
                        });
                        // Mark as read immediately
                        supabase.from("messages")
                            .update({ is_read: true })
                            .eq("id", newMsg.id);
                        setTimeout(() => {
                            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
                        }, 50);
                    }
                }
            )
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [conversation?.id, user]);

    // ── RESET on conversation change ──────────────────────────────────────────
    useEffect(() => {
        if (!conversation || !user) {
            setMessages([]);
            return;
        }
        setMessages([]);
        fetchInitial();
    }, [conversation?.id]);

    // ── SEND ──────────────────────────────────────────────────────────────────
    const sendMessage = async () => {
        if (!text.trim() || !conversation) return;

        const tempId = crypto.randomUUID();
        const newMessage = {
            id: tempId,
            conversation_id: conversation.id,
            sender_id: user.id,
            content: text.trim(),
            created_at: new Date().toISOString(),
            is_read: false,
        };

        setMessages(prev => [...prev, newMessage]);
        if (onMessageChange) onMessageChange(conversation.id, newMessage);
        setText("");

        setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 30);

        const { data: inserted } = await supabase
            .from("messages")
            .insert([{
                conversation_id: conversation.id,
                sender_id: user.id,
                content: newMessage.content,
            }])
            .select()
            .single();

        // Replace temp message with real one (real id from DB)
        if (inserted) {
            setMessages(prev =>
                prev.map(m => m.id === tempId ? inserted : m)
            );
        }
    };

    // ── DELETE CONVERSATION ────────────────────────────────────────────────────
    const deleteConversation = async () => {
        if (!conversation) return;
        await supabase.from("conversations").delete().eq("id", conversation.id);
        setShowDeleteConfirm(false);
        setShowDetailsPanel(false);
        setMessages([]);
        navigate("/messages");
    };

    // ── MESSAGE MENU ──────────────────────────────────────────────────────────
    const openMenu = (e, msgId) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const menuWidth = 220;
        let left = rect.left;
        let top = rect.bottom + 6;
        if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 10;
        if (left < 10) left = 10;
        if (rect.bottom + 200 > window.innerHeight) top = rect.top - 200 - 6;
        setMenuState({ id: msgId, top, left });
    };

    useEffect(() => {
        const close = () => setMenuState({ id: null, top: 0, left: 0 });
        window.addEventListener("click", close);
        return () => window.removeEventListener("click", close);
    }, []);

    const copyMessage = async (txt) => {
        await navigator.clipboard.writeText(txt);
        setMenuState({ id: null, top: 0, left: 0 });
    };

    const unsendMessage = async (msgId) => {
        await supabase.from("messages").delete().eq("id", msgId).eq("sender_id", user.id);
        setMessages(prev => prev.filter(m => m.id !== msgId));
        setMenuState({ id: null, top: 0, left: 0 });
        const remaining = messages.filter(m => m.id !== msgId);
        if (onMessageChange) onMessageChange(conversation.id, remaining[remaining.length - 1] || null);
    };

    // ── DATE HELPERS ──────────────────────────────────────────────────────────
    const formatDateHeader = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const msgDate = new Date(date); msgDate.setHours(0,0,0,0);
        if (msgDate >= startOfWeek) return msgDate.toLocaleDateString("en-US", { weekday: "long" });
        return msgDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    };

    const formatTime = (ds) => new Date(ds).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

    const renderMenu = (msg) => msg ? (
        <>
            <div className="chat-option-date">
                {formatDateHeader(msg.created_at)}
                <span>{formatTime(msg.created_at)}</span>
            </div>
            <div className="divider" />
            <div className="chat-option-item" onClick={() => copyMessage(msg.content)}>Copy</div>
            {msg.sender_id === user.id && (
                <>
                    <div className="divider" />
                    <div className="chat-option-item danger" onClick={() => unsendMessage(msg.id)}>Unsend</div>
                </>
            )}
        </>
    ) : null;

    return (
        <>
            <div className="messages-right">

                {/* NO CONVERSATION */}
                {!conversation ? (
                    <div className="chat-empty">
                        <svg fill="currentColor" height="96" viewBox="0 0 96 96" width="96">
                            <circle cx="48" cy="48" fill="none" r="47" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            <path d="m52.309 67.221 17.024-28.643c2.25-3.784-.478-8.578-4.88-8.578H31.55c-5.084 0-7.605 6.169-3.976 9.73l10.574 10.376 3.762 15.55c1.197 4.947 7.798 5.94 10.399 1.565Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
                            <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="38.148" x2="55.675" y1="50.106" y2="40.134" />
                        </svg>
                        <h3>Your Messages</h3>
                        <p>Send a message to start a chat.</p>
                        <button className="messages-start-btn" onClick={() => setShowNewMessage(true)}>Send Message</button>
                    </div>
                ) : (
                    <>
                        {/* HEADER */}
                        <div className="chat-header">
                            <div className="chat-user-info clickable-header" onClick={() => navigate(`/user/${conversation.otherUser?.username}`)}>
                                {conversation.otherUser?.avatar_url ? (
                                    <img src={conversation.otherUser.avatar_url} className="chat-avatar" alt="" />
                                ) : (
                                    <div className="chat-avatar fallback">
                                        {conversation.otherUser?.username?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="chat-user-text">
                                    <div className="chat-name">{conversation.otherUser?.full_name || conversation.otherUser?.username}</div>
                                    <div className="chat-username">@{conversation.otherUser?.username}</div>
                                </div>
                            </div>
                            <button className="chat-info-btn" onClick={() => setShowDetailsPanel(true)}>
                                <svg aria-label="Conversation information" fill="currentColor" height="24" viewBox="0 0 24 24" width="24">
                                    <circle cx="12.001" cy="12.005" fill="none" r="10.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                                    <circle cx="11.819" cy="7.709" r="1.25" />
                                    <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="10.569" x2="13.432" y1="16.777" y2="16.777" />
                                    <polyline fill="none" points="10.569 11.05 12 11.05 12 16.777" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                                </svg>
                            </button>
                        </div>

                        {/* MESSAGES */}
                        <div className="chat-messages" ref={chatMessagesRef}>

                            {/* Load more spinner at top */}
                            {loadingMore && (
                                <div className="chat-load-more-spinner">
                                    <div className="chat-spinner" />
                                </div>
                            )}

                            {/* End of history */}
                            {!hasMore && !loadingInitial && messages.length > 0 && (
                                <div className="chat-history-end">Beginning of conversation</div>
                            )}

                            {/* Profile intro block */}
                            {!loadingInitial && (
                                <div className="chat-user-intro">
                                    {conversation.otherUser?.avatar_url ? (
                                        <img src={conversation.otherUser.avatar_url} className="chat-intro-avatar large" alt="" />
                                    ) : (
                                        <div className="chat-intro-avatar fallback large">
                                            {conversation.otherUser?.username?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="chat-intro-text">
                                        <div className="chat-intro-name">{conversation.otherUser?.full_name || conversation.otherUser?.username}</div>
                                        <div className="chat-intro-username">@{conversation.otherUser?.username} · CodeGram</div>
                                        <button className="view-profile-btn" onClick={() => navigate(`/user/${conversation.otherUser?.username}`)}>
                                            View Profile
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Messages */}
                            {messages.map((msg, i) => {
                                const showDate = i === 0 ||
                                    new Date(messages[i - 1].created_at).toDateString() !== new Date(msg.created_at).toDateString();

                                return (
                                    <React.Fragment key={msg.id}>
                                        {showDate && (
                                            <div className="chat-date-divider">{formatDateHeader(msg.created_at)}</div>
                                        )}
                                        <div className={`chat-bubble-wrapper ${msg.sender_id === user.id ? "mine" : "other"}`}>
                                            {msg.sender_id === user.id && (
                                                <div className="chat-options-container">
                                                    <div className="chat-options-btn" onClick={(e) => openMenu(e, msg.id)}>⋯</div>
                                                </div>
                                            )}

                                            {/* POST MESSAGE — thumbnail card */}
                                            {msg.message_type === "post" && msg.post_id ? (
                                                <PostMessageBubble
                                                    msg={msg}
                                                    isMine={msg.sender_id === user.id}
                                                    onOpenPost={(postData, postProfile) =>
                                                        setSelectedPostMsg({ post: postData, profile: postProfile })
                                                    }
                                                />
                                            ) : (
                                                <div className={`chat-bubble ${msg.sender_id === user.id ? "mine" : "other"}`}>
                                                    {msg.content}
                                                </div>
                                            )}

                                            {msg.sender_id !== user.id && (
                                                <div className="chat-options-container">
                                                    <div className="chat-options-btn" onClick={(e) => openMenu(e, msg.id)}>⋯</div>
                                                </div>
                                            )}
                                        </div>
                                    </React.Fragment>
                                );
                            })}

                            {/* Initial loading skeletons — shown at bottom so they look like latest messages loading in */}
                            {loadingInitial && (
                                <div className="chat-initial-loading">
                                    <div className="chat-msg-skeleton other w-40" />
                                    <div className="chat-msg-skeleton mine w-55" />
                                    <div className="chat-msg-skeleton other w-30" />
                                    <div className="chat-msg-skeleton mine w-65" />
                                    <div className="chat-msg-skeleton other w-45" />
                                    <div className="chat-msg-skeleton mine w-35" />
                                </div>
                            )}

                            <div ref={bottomRef} />

                            {menuState.id && (
                                <div
                                    className="chat-options-menu"
                                    style={{ top: menuState.top, left: menuState.left }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    {renderMenu(messages.find(m => m.id === menuState.id))}
                                </div>
                            )}
                        </div>

                        {/* INPUT */}
                        <div className="chat-input">
                            <div className="chat-input-wrapper">
                                <button className="chat-emoji-btn" onClick={() => setShowEmoji(p => !p)}>
                                    <FontAwesomeIcon icon={faFaceSmile} />
                                </button>
                                <input
                                    ref={inputRef}
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
                                    placeholder="Message..."
                                />
                                {text.trim() && (
                                    <button className="chat-send-btn" onClick={sendMessage}>Send</button>
                                )}
                            </div>

                            {showEmoji && (
                                <div className="emoji-popup">
                                    {["😀","😁","😂","🤣","😍","😎","😢","😡","👍","❤️","🎉","🔥","🥳","👏","🤔","🙌","😇","🤩"].map((emoji, i) => (
                                        <span key={i} onClick={() => {
                                            setText(p => p + emoji);
                                            setShowEmoji(false);
                                            setTimeout(() => inputRef.current?.focus(), 0);
                                        }}>
                                            {emoji}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* DETAILS PANEL */}
            {showDetailsPanel && (
                <>
                    <div className="details-overlay" onClick={() => setShowDetailsPanel(false)} />
                    <div className="details-panel">
                        <div className="details-header">Details</div>
                        <div className="details-divider" />
                        <div className="details-section-title">Members</div>
                        <div className="details-member" onClick={() => window.open(`/user/${conversation.otherUser?.username}`, "_blank")}>
                            {conversation.otherUser?.avatar_url ? (
                                <img src={conversation.otherUser.avatar_url} className="details-avatar" alt="" />
                            ) : (
                                <div className="details-avatar fallback">
                                    {conversation.otherUser?.username?.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div className="details-member-text">
                                <div className="details-name">{conversation.otherUser?.full_name || conversation.otherUser?.username}</div>
                                <div className="details-username">@{conversation.otherUser?.username}</div>
                            </div>
                        </div>
                        <div className="details-bottom">
                            <div className="details-divider" />
                            <button className="delete-chat-btn" onClick={() => setShowDeleteConfirm(true)}>Delete Chat</button>
                        </div>
                    </div>
                </>
            )}

            {/* DELETE CONFIRM */}
            {showDeleteConfirm && (
                <div className="delete-chat-overlay">
                    <div className="delete-chat-box">
                        <div className="delete-chat-content">
                            <h3>Delete chat from inbox?</h3>
                            <p>This will remove the chat from your inbox and erase the chat history. To stop receiving new messages from this account, first block the account then delete the chat.</p>
                        </div>
                        <div className="delete-divider" />
                        <button className="delete-confirm-btn" onClick={deleteConversation}>Delete</button>
                        <div className="delete-divider" />
                        <button className="delete-cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                    </div>
                </div>
            )}

            <NewMessageModal open={showNewMessage} onClose={() => setShowNewMessage(false)} />

            {/* POST VIEWER — opened when clicking a shared post in chat */}
            {selectedPostMsg && (
                <PostViewerModal
                    open={!!selectedPostMsg}
                    onClose={() => setSelectedPostMsg(null)}
                    post={selectedPostMsg.post}
                    posts={[selectedPostMsg.post]}
                    profile={selectedPostMsg.profile}
                    onNextPost={() => {}}
                    onPrevPost={() => {}}
                    onPostDeleted={() => setSelectedPostMsg(null)}
                    onPostUpdated={() => {}}
                />
            )}
        </>
    );
}

// ── POST MESSAGE BUBBLE ────────────────────────────────────────────────────────
// Renders a shared post as a thumbnail card inside the chat.
// Fetches post data (image + author) on mount, then opens PostViewerModal on click.
function PostMessageBubble({ msg, isMine, onOpenPost }) {
    const [postData, setPostData] = useState(null);
    const [postProfile, setPostProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!msg.post_id) return;
        const load = async () => {
            const { data, error } = await supabase
                .from("posts")
                .select(`
                    id, caption, created_at, hide_like_counts, turn_off_commenting, user_id,
                    profiles:user_id(id, username, avatar_url),
                    post_images(id, image_path, order_index),
                    likes(user_id)
                `)
                .eq("id", msg.post_id)
                .single();

            if (error || !data) { setLoading(false); return; }

            // Sign image URLs
            const sorted = (data.post_images || []).sort((a, b) => a.order_index - b.order_index);
            const withUrls = await Promise.all(sorted.map(async (img) => {
                const { data: sd } = await supabase.storage
                    .from("posts").createSignedUrl(img.image_path, 3600);
                return { ...img, signedUrl: sd?.signedUrl || null };
            }));

            const formatted = {
                ...data,
                post_images: withUrls,
                likesCount: data.likes?.length || 0,
                isLiked: false,
            };

            setPostData(formatted);
            setPostProfile(data.profiles);
            setLoading(false);
        };
        load();
    }, [msg.post_id]);

    const thumbUrl = postData?.post_images?.[0]?.signedUrl;

    return (
        <div
            className={`chat-post-bubble ${isMine ? "mine" : "other"}`}
            onClick={() => postData && onOpenPost(postData, postProfile)}
        >
            {loading ? (
                <div className="chat-post-skeleton" />
            ) : postData ? (
                <>
                    {thumbUrl && (
                        <img src={thumbUrl} className="chat-post-thumb" alt="" />
                    )}
                    <div className="chat-post-info">
                        <span className="chat-post-author">@{postProfile?.username}</span>
                        {postData.caption && (
                            <p className="chat-post-caption">
                                {postData.caption.length > 60
                                    ? postData.caption.slice(0, 60) + "…"
                                    : postData.caption}
                            </p>
                        )}
                    </div>
                    {msg.content && msg.content !== "Sent a post" && (
                        <div className="chat-post-text-overlay">{msg.content}</div>
                    )}
                </>
            ) : (
                <div className="chat-post-deleted">Post unavailable</div>
            )}
        </div>
    );
}

export default ChatPanel;